import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IChat } from '@common/models/Chat/IChat';
import { Observable, Subject } from 'rxjs';
import { ApiService } from 'quangular';
import { IDocument } from '@common/models/Document/IDocument';
import { IChatInteraction } from '@common/models/Chat/IChatInteraction';
import { IChatDocument } from '@common/models/Chat/IChatDocument';
import { IChatRequest } from '@common/models/Chat/IChatRequest';
import { IAddDocumentToChat } from '@common/models/Chat/IAddDocumentToChat';
import { IAddDoctoChatAPIResp } from '@common/models/Chat/IAddDoctoChatAPIResp';
import { IInteractionFeedback } from '@common/models/Chat/IInteractionFeedback';
import { InteractionType } from '@common/enums/InteractionType';
import { ChatType } from '@common/enums/ChatType';
import { IDeltaTable } from '@common/models/Document/IDeltaTable';
import { IESGDocuments, IResponseESGFilters } from '@common/models/Document/IESGDocuments';
import { ITableTag } from "@common/models/ITags";
import { IUser } from '@common/models/IUser';
import { IChatConfig } from '@common/models/Chat/IChatConfig';
import { ITableRow } from '@common/models/Document/ITableRow';
import { ICustomInstructions } from '@common/models/Chat/ICustomInstructions';
import { IModelRegistry } from '@common/models/ModelRegistry/IModelRegistry';
import { ReasoningModeInput } from '@app/types';
import { EXCEL_FILE_FORMATS } from '@common/constants';
import { PromptTemplateType } from '@app/types/PromptTemplate';

export interface IChatSelectedEvent {
    type: string;
    activeChat: IChat;
    chatId: string;
    period?: string;
    interaction?: IChatInteraction;
}

export interface IAllChatsPeriod {
    period: string;
    showChats: boolean;
    chats: IChat[];
    startDate: number;
    endDate: number;
    isBookmarks?: boolean;
    name?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ChatServices {
    private activeChat: IChat;
    private readonly activeChatObs = new Subject<IChat>();
    private chatDocumentsList: IDocument [] = [];
    public chatType:ChatType = ChatType.Normal;
    private readonly chatTypeObs = new Subject<ChatType>();
    public interactionType : ChatType = ChatType.Normal;
    public chatConfig :IChatConfig = {};
    public memoryChat: boolean = false;
    public selectedModel: string | null = null;
    public reasoningModeSelection: ReasoningModeInput | null = null;
    public promptTemplateSelection: PromptTemplateType | null = null;
    private readonly chatSelectedSubject = new Subject<IChatSelectedEvent>();
    private allChats: IAllChatsPeriod[] = [];
    private readonly allChatsSubject = new Subject<IAllChatsPeriod[]>();
    private readonly createChatSubject = new Subject<IChat | void>();
    private readonly updateChatTypeSubject = new Subject<{label: string, type: ChatType}>();

    constructor(private readonly http: HttpClient, private readonly apiService: ApiService) {}
    getAllChats(start_date: number, end_date: number,isBookmarks:boolean): Observable<IChat[]> {
        return this.apiService.get<IChat[]>(`chats?start_date=${start_date}&end_date=${end_date}&chatType=${this.chatType}&isBookmarks=${isBookmarks}`);
    }
    getchatById(chatId:string): Observable<IChat> {
      return this.apiService.get<IChat>(`chats/${chatId}/details`);
  }
    getChatImage(chatId:string,uuidImageName:string): Observable<ArrayBuffer> {
      return this.apiService.get<ArrayBuffer>(`chats/${chatId}/images/${uuidImageName}`);
    }
    // getSearchChats(searchText:string): Observable<IChat[]> {
    //   return this.apiService.get<IChat[]>(`Chat?searchText=${searchText}`);
    // }
    // getSearchChatInteractions(): Observable<IChatInteraction[]> {
    //   return this.apiService.get<IChatInteraction[]>(`Chat`);
    // }
    //   getSingleChat(chatId:string): Observable<IChat> {
    //     return this.apiService.get<IChat>(`Chat/${chatId}` );
    // }


    /**
     * Create a new chat, atomically assigning the active prompt template if available.
     * @param promptTemplate Optional prompt template object ({ name, label, description }) to assign to the chat.
     */
    createNewChat(promptTemplate?: PromptTemplateType): Observable<IChat> {
      let postBody: Partial<IChat> = {};

      // Always include chatType
      postBody['chatType'] = this.chatType;
      postBody['chatConfig'] = {}

      // Include memoryChat if set
      if (this.memoryChat) {
        postBody['chatConfig'] = { memoryChat: this.memoryChat };
      }

      // Include chatConfig for ESG
      if (
        (this.chatType === ChatType.ESG && this.chatConfig) ||
        this.chatType === ChatType.QEC ||
        this.chatType === ChatType.codeInterpreter ||
        this.chatType === ChatType.HRDocuments ||
        this.chatType === ChatType.agenticWorkflow ||
        this.chatType === ChatType.deepResearch
      ) {
        postBody['chatType'] = this.chatType;
        if (this.chatType === ChatType.ESG && this.chatConfig) {
          postBody['chatConfig'] = this.chatConfig;
        }
      }

      if (this.selectedModel) {
        postBody['selectedModel'] = this.selectedModel;
      }

      // Determine the prompt template to use: parameter > service selection > null
      const activePrompt =
        promptTemplate?.name ||
        this.promptTemplateSelection?.name ||
        null;

      if (activePrompt) {
        postBody['activePromptTemplate'] = activePrompt;
      }

      if(this.reasoningModeSelection){
        postBody['chatConfig']['reasoningMode'] = this.reasoningModeSelection.value;
      }

      return this.apiService.post('chats', postBody);
    }

    getChatTypeObs(): Observable<ChatType> {
        return this.chatTypeObs.asObservable();
    }

    updateChatTypeStatus(chatType:ChatType) {
      this.chatType = chatType
      this.chatTypeObs.next(chatType);
    }
    getChatType():ChatType {
      return this.chatType
    }
    getChatInteractions(params: string): Observable<IChatInteraction[]> {
        return this.apiService.get<IChatInteraction[]>(`chats/${params}`);
    }
    deleteChat(chatId: string): Observable<Response> {
        return this.apiService.delete(`chats/${chatId}`);
    }
    sendQuestion(inputText: string, chatId: string, interactionType: InteractionType, isEdit?: boolean, editInteractionId?: string): Observable<IChatInteraction> {
        const payload: IChatRequest = {
            requestText: inputText,
            askedTime: new Date(),
        };
        const params = new URLSearchParams({ interactionType });
        if (isEdit) params.set('isEdit', String(isEdit));
        if (editInteractionId) params.set('editInteractionId', editInteractionId);

        return this.apiService.post(`chats/${chatId}/questions?${params.toString()}`, payload);
    }
    getQuestionResponse(
        chatId: string,
        questionId: string,
        regeneration:boolean,
        documentChat:ChatType,
        chatModel?:IModelRegistry | null
    ): Observable<IChatInteraction> {
      const domainSpecificChatTypes: ChatType[] = [
        ChatType.QEC,
        ChatType.codeInterpreter,
        ChatType.HRDocuments,
        ChatType.deepResearch,
        ChatType.quilt
      ] 
      if(domainSpecificChatTypes.includes(this.chatType) || this.activeChat?.chatType === ChatType.QEC){
        documentChat = this.chatType
      }
      if(this.chatType === ChatType.ESG && documentChat!==ChatType.ESGStructuredUnstructuredDocs){
        documentChat = ChatType.ESG
      }
      const val = chatModel?.model ? { selectedModel: chatModel.model } : null;
      this.interactionType = documentChat
        return this.apiService.post(
        `chats/${chatId}/interactions/${questionId}?documentChat=${documentChat}&regenerate=${regeneration}`,
        val
      ) as Observable<IChatInteraction>;
    }
    getQuestionResponseSummary(
        chatId: string,
        questionId: string,
        documentId:string,
        documentChat:ChatType,
        selectedModel: string | null
    ): Observable<IChatInteraction> {
      if(this.chatType === ChatType.QEC || this.activeChat?.chatType === ChatType.QEC || this.chatType === ChatType.codeInterpreter || this.chatType === ChatType.deepResearch){
        documentChat = this.chatType
      }
      if(this.chatType === ChatType.ESG){
        documentChat = ChatType.ESG
      }
      this.interactionType = documentChat
        return this.apiService.post(
            `chats/${chatId}/interactions/${questionId}?documentChat=${documentChat}&documentId=${documentId}`,{ selectedModel }
        ) as Observable<IChatInteraction>;
    }

    chatFeedback(chatId:string,interactionId:string,responseIndex:number,payload:IInteractionFeedback): Observable<IChatInteraction> {
        const query = responseIndex !== undefined && responseIndex !== null ? `?responseIndex=${responseIndex}` : '';
        return this.apiService.post( `chats/${chatId}/interactions/${interactionId}/feedback${query}`, payload);
    }

    getChatDocuments(chatId: string): Observable<IDocument[]> {
        return this.apiService.get<IDocument[]>(`documents/chats/${chatId}`);
    }

    getDocumentDetails(docId: string,chatId:string, type:string): Observable<IDocument> {
      if(EXCEL_FILE_FORMATS.includes(type)){
        return this.apiService.get<IDocument>(`documents/${docId}?chatId=${chatId}`);
      }else{
        return this.apiService.get<IDocument>(`documents/${docId}`);
      }
    }

    UserMe(): Observable<IUser> {
        return this.apiService.get<IUser>('users/me');
    }
    uploadFile(params: FormData, forceUpload?: boolean): Observable<IDocument> {
        let url = `documents`;
        if (forceUpload) {
            url = `documents?forceUpload=${forceUpload}`;
        }
        return this.apiService.post(url, params);
    }

    sendFileQuestion(params: FormData,chatId:string): Observable<IChatInteraction> {
      return this.apiService.post(`chats/${chatId}/file-question`, params);
    }

    retriggerUploadFile(doc_Id:string): Observable<IDocument> {
        let url = `documents/${doc_Id}/retrigger`;
        return this.apiService.put(url,{});
    }

    updateChatDocuments(
        docs: IChatDocument[],
        chatId: string,
        tables?: IDeltaTable[],
        esgTags? : ITableTag[], chatConfig? : IChatConfig
    ): Observable<IAddDoctoChatAPIResp> {
        const payload: IAddDocumentToChat = {
            document: docs,
        };
        if(tables && tables.length > 0){
          payload.tables = tables
        }
        if(esgTags){
          payload.ESGTags = esgTags
        }
        if(chatConfig){
          payload.chatConfig = chatConfig
        }

        return this.apiService.post(`documents/chats/${chatId}`, payload);
    }

    deleteChatDocs(chatId: string, docId: string): Observable<Response> {
        return this.apiService.delete(
            `documents/chats/${chatId}/documents/${docId}`
        );
    }

    getActiveChatObs(): Observable<IChat> {
        return this.activeChatObs.asObservable();
    }

    updateActiveChatObs(docs: IChat) {
        this.activeChatObs.next(docs);
    }

    getChatDocumentList() {
        return this.chatDocumentsList;
    }

    updateChatDocumentList(docs: IDocument[]) {
        this.chatDocumentsList = docs;
    }

    updateActiveChat(chat: IChat) {
        this.activeChat = chat;
    }

    setModelSelection(model: IModelRegistry) {
        this.selectedModel = model?.model ?? null;
    }

    setPromptTemplateSelection(template: PromptTemplateType) {
        this.promptTemplateSelection = template;
    }

    setReasoningModeSelection(reasoningMode: ReasoningModeInput){
      this.reasoningModeSelection = reasoningMode;
    }

    getPromptTemplateSelection() {
        return this.promptTemplateSelection;
    }

    getActiveChat() {
        return this.activeChat;
    }

    editChatDetails(chatId:string,payload:Partial<IChat>, blockUpdatedAt = false){
        return this.apiService.put(`chats/${chatId}?blockUpdatedAt=${blockUpdatedAt}`,payload)
    }

    getAllDocs(search?: string,markerId?:number): Observable<IDocument[]> {
        let url=`documents?pageSize=15`
        if(markerId){
            url=`documents?pageSize=15&markerId=${markerId}`
        }
        if(search && search.trim()!=''){
            url=`documents?searchText=${search.trim()}`
        }
        return this.apiService.get<IDocument[]>(
           url
        );
    }

    getChatInteractionById(chatId: string, interactionId:string): Observable<IChatInteraction> {
      return this.apiService.get<IChatInteraction>(`chats/${chatId}/interactions/${interactionId}`);
  }

  checkStatusOfLLMInteraction(chatId: string, interactionId:string): Observable<IChatInteraction> {
    return this.apiService.get<IChatInteraction>(`chats/${chatId}/interactions/${interactionId}/status`);
  }

  cancelGeneration(chatId: string, interactionId:string): Observable<IChatInteraction> {
    return this.apiService.put(`chats/${chatId}/interactions/${interactionId}/cancel`, {});
  }

  getDeltaTableById(tableId:string): Observable<IDeltaTable> {
    return this.apiService.get<IDeltaTable>(`delta-tables/${tableId}`);
  }

  getDeltaTableByMultipleIDs(tableId:string): Observable<IDeltaTable> {
    return this.apiService.get<IDeltaTable>(`delta-tables/${tableId}`);
  }

  getESGDocuments(): Observable<IResponseESGFilters> {
    return this.apiService.get<IResponseESGFilters>(`delta-tables/esg/documents`);
  }

  getESGDocumentByID(documentId:string): Observable<IESGDocuments> {
    return this.apiService.get<IESGDocuments>(`delta-tables/esg/documents/${documentId}`);
  }

  getDataBySqlCode(sqlCode: string): Observable<ITableRow> {
    return this.apiService.get<ITableRow>(`chats/interactions/sql-code-data?sqlCode=${sqlCode}`);
  }

  getHRDocuments(): Observable<IChatDocument[]> {
    return this.apiService.get<IChatDocument[]>(`chats/hr/documents`);
  }

  getcustomInstructions(): Observable<ICustomInstructions> {
    return this.apiService.get<ICustomInstructions>(`chats/user/custom-instructions?chatType=${this.chatType}`);
  }

  saveCustomInstructions(customInstructions:Partial<ICustomInstructions>): Observable<ICustomInstructions> {
    return this.apiService.put(`chats/user/custom-instructions/${this.chatType}`,customInstructions);
  }

   updateUserModel(partialUser:Partial<IUser>): Observable<IUser> {
    return this.apiService.put(`users/me`,partialUser);
  }

  searchChats(payload: any): Observable<any[] | "No Results"> {
    const queryParams = Object.keys(payload)
      .map((key) => `${key}=${encodeURIComponent(payload[key])}`)
      .join('&');

    return this.apiService.get<any[] | "No Results">(`chats/interactions/search?${queryParams}`);
  }

  fetchAllModels(): Observable<IModelRegistry[]> {
        return this.apiService.get<IModelRegistry[]>(`models`);
  }

  getChatSelectedObs(): Observable<IChatSelectedEvent> {
        return this.chatSelectedSubject.asObservable();
  }

  emitChatSelected(event: IChatSelectedEvent): void {
        this.chatSelectedSubject.next(event);
  }

  getAllChatsObs(): Observable<IAllChatsPeriod[]> {
        return this.allChatsSubject.asObservable();
  }

  updateAllChats(chats: IAllChatsPeriod[]): void {
        this.allChats = chats;
        this.allChatsSubject.next(chats);
  }

  getAllChatsValue(): IAllChatsPeriod[] {
        return this.allChats;
  }

  getCreateChatObs(): Observable<IChat | void> {
        return this.createChatSubject.asObservable();
  }

  emitCreateChat(chat?: IChat): void {
        this.createChatSubject.next(chat);
  }

  getUpdateChatTypeObs(): Observable<{label: string, type: ChatType}> {
        return this.updateChatTypeSubject.asObservable();
  }

  emitUpdateChatType(chatType: {label: string, type: ChatType}): void {
        this.updateChatTypeSubject.next(chatType);
  }

}
